// Web-Push im Profil (RFC 0022).
//
// Der Zustand steht im Browser, nicht auf dem Server: ob *dieses* Geraet abonniert
// hat, weiss nur der Browser (`pushManager.getSubscription()`), und die Erlaubnis
// kennt ohnehin nur er. Der Server erfaehrt beides nur, wenn sich etwas aendert —
// deshalb schreibt der Hook den Text des Schalters selbst und schickt bloss Abo und
// Abmeldung hoch. Die Texte kommen als data-Attribute rein, damit sie durch Gettext
// laufen.

// Der VAPID-Schluessel kommt als base64url. `applicationServerKey` nimmt laut Spec
// auch den String — aber nicht jeder Browser haelt sich daran, und ein Uint8Array
// nehmen alle.
const urlB64ToUint8Array = (base64) => {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, "+")
    .replace(/_/g, "/")
  const raw = atob(padded)
  return Uint8Array.from(raw, (c) => c.charCodeAt(0))
}

// Push gibt es nur mit Service Worker und PushManager. Auf iOS existiert beides
// ausschliesslich in der installierten PWA — im normalen Safari-Tab fehlt der
// PushManager, und genau daran erkennen wir den Fall (ohne User-Agent zu raten).
const supported = () => "serviceWorker" in navigator && "PushManager" in window

// Standalone heisst: vom Homescreen gestartet. Nur dort kann iOS ueberhaupt.
const standalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true

export const PushToggle = {
  mounted() {
    this.render()
    this.el.addEventListener("click", () => this.toggle())
  },

  // Der Server schickt nach jedem Ereignis ein Update — der Knopf muss danach
  // wieder das sagen, was der Browser gerade denkt.
  updated() {
    this.render()
  },

  async subscription() {
    if (!supported()) return null
    const registration = await navigator.serviceWorker.getRegistration()
    return registration ? await registration.pushManager.getSubscription() : null
  },

  async render() {
    const hint = document.getElementById(this.el.dataset.hintEl)
    const wrap = document.getElementById(`${this.el.id}-wrap`)
    const say = (text) => hint && (hint.textContent = text)

    // Der ganze Block, samt Lede-Zeile — nicht nur der Knopf.
    const showBlock = (visible) => wrap && (wrap.hidden = !visible)

    showBlock(true)

    // Auf iOS im Browser-Tab gibt es keinen Weg — dann kein toter Schalter, sondern
    // der Satz, der tatsaechlich weiterhilft.
    if (!supported()) {
      this.el.hidden = true
      say(standalone() ? this.el.dataset.unsupportedText : this.el.dataset.installFirstText)
      return
    }

    if (Notification.permission === "denied") {
      this.el.hidden = true
      say(this.el.dataset.deniedText)
      return
    }

    const subscription = await this.subscription()

    // Auf dem Weglauf-Bildschirm (RFC 0023) verschwindet der Vorschlag ganz, sobald
    // dieses Geraet abonniert hat: die Frage ist beantwortet. Ein "nicht mehr
    // benachrichtigen" ausgerechnet dort waere eine Einladung zum Falschen. Im Profil
    // (ohne `hideWhenActive`) bleibt er stehen — dort sucht man ja Einstellungen.
    if (subscription && this.el.dataset.hideWhenActive) {
      say("")
      showBlock(false)
      return
    }

    this.el.hidden = false
    this.el.disabled = false
    this.el.textContent = subscription ? this.el.dataset.offText : this.el.dataset.onText
    say(subscription ? this.el.dataset.activeText : this.el.dataset.inactiveText)

    // Selbstheilung: der Browser hat ein Abo, der Server vielleicht nicht mehr (eine
    // 410-Antwort hat die Zeile geloescht, das Geraet weiss davon nichts). Einmal
    // hochschicken ist idempotent — Upsert auf den endpoint.
    if (subscription) this.pushEvent("push_subscribe", subscription.toJSON())
  },

  async toggle() {
    this.el.disabled = true

    try {
      const existing = await this.subscription()

      if (existing) {
        const endpoint = existing.endpoint
        await existing.unsubscribe()
        this.pushEvent("push_unsubscribe", { endpoint })
      } else {
        // Erlaubnis nur auf Klick fragen. Ein Prompt beim Laden ist die zuverlaessigste
        // Art, ein "Blockieren" zu kassieren — und Safari verlangt ohnehin eine Geste.
        const permission = await Notification.requestPermission()
        if (permission !== "granted") return this.render()

        // Registriert wird der Worker nicht hier — das macht app.js schon beim
        // Laden, fuer alle (#169). `ready` wartet nur, bis das steht.
        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.subscribe({
          // Ohne sichtbare Benachrichtigung kein Push — Pflicht in allen Browsern,
          // und die richtige Regel: stiller Hintergrund-Push waere ein Tracker.
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(this.el.dataset.vapidKey),
        })

        this.pushEvent("push_subscribe", subscription.toJSON())
      }
    } catch (_e) {
      // Fehlgeschlagen ist fehlgeschlagen — der Browser sagt nicht warum, und der
      // Nutzer kann nichts damit anfangen. Der Knopf zeigt einfach wieder den Stand.
    }

    this.render()
  },
}
